
import React, { useState, useEffect } from 'react';
import { FutureRelease, FutureReleaseFormData, InstallmentGroup } from '@/types';
import { FutureReleasesContext } from './FutureReleasesContext';
import { createFutureRelease, updateFutureReleaseData } from './operations';
import { useAuth } from '@/contexts/auth';
import { useProfile } from '@/contexts/profile';
import { useEncryption } from '@/contexts/encryption';
import {
  saveFutureReleaseToSupabase,
  loadFutureReleasesFromSupabase,
  deleteFutureReleaseFromSupabase,
  saveInstallmentGroupToSupabase,
  loadInstallmentGroupsFromSupabase,
  deleteInstallmentGroupFromSupabase,
  isUserAuthenticated
} from '@/lib/supabase-future-releases-operations';
import { migrateFutureReleasesToSupabase } from '@/lib/data-migration';

export const FutureReleasesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [futureReleases, setFutureReleases] = useState<FutureRelease[]>([]);
  const [installmentGroups, setInstallmentGroups] = useState<InstallmentGroup[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  const { activeProfile } = useProfile();
  const { isEncryptionReady, isCheckingEncryption } = useEncryption();

  // Load data when user changes
  useEffect(() => {
    if (!user) {
      setFutureReleases([]);
      setInstallmentGroups([]);
      return;
    }

    if (isCheckingEncryption) {
      console.log('[FutureReleases] Waiting for encryption check...');
      return;
    }
    
    if (!isEncryptionReady) {
      console.warn('[FutureReleases] Encryption not ready, skipping load');
      setIsLoading(false);
      return;
    }

    const loadData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        // Perform migration first
        await migrateFutureReleasesToSupabase();
        
        if (await isUserAuthenticated()) {
          console.log('[FutureReleases] Loading data from Supabase...');
          
          // Load releases and groups concurrently
          const [supabaseReleases, supabaseGroups] = await Promise.all([
            loadFutureReleasesFromSupabase(),
            loadInstallmentGroupsFromSupabase()
          ]);
          
          setFutureReleases(supabaseReleases);
          setInstallmentGroups(supabaseGroups);
          
          console.log(`[FutureReleases] Loaded ${supabaseReleases.length} releases and ${supabaseGroups.length} groups from Supabase`);
        } else {
          // Fallback to localStorage
          console.log('[FutureReleases] Loading data from localStorage...');
          const localReleases = localStorage.getItem('futureReleases');
          const localGroups = localStorage.getItem('installmentGroups');
          
          setFutureReleases(localReleases ? JSON.parse(localReleases) : []);
          setInstallmentGroups(localGroups ? JSON.parse(localGroups) : []);
        }
      } catch (error) {
        console.error('[FutureReleases] Error loading data:', error);
        setError('Erro ao carregar lançamentos');
        // Fallback to localStorage on error
        const localReleases = localStorage.getItem('futureReleases');
        const localGroups = localStorage.getItem('installmentGroups');
        
        setFutureReleases(localReleases ? JSON.parse(localReleases) : []);
        setInstallmentGroups(localGroups ? JSON.parse(localGroups) : []);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [user, isEncryptionReady, isCheckingEncryption]);

  // Reload data when active profile changes
  useEffect(() => {
    if (user && activeProfile && isEncryptionReady && !isCheckingEncryption) {
      console.log('[FutureReleases] Profile changed, reloading data');
      const loadData = async () => {
        setIsLoading(true);
        setError(null);
        try {
          const [supabaseReleases, supabaseGroups] = await Promise.all([
            loadFutureReleasesFromSupabase(),
            loadInstallmentGroupsFromSupabase()
          ]);
          
          setFutureReleases(supabaseReleases);
          setInstallmentGroups(supabaseGroups);
          console.log(`[FutureReleases] Reloaded ${supabaseReleases.length} releases and ${supabaseGroups.length} groups for profile: ${activeProfile.name}`);
        } catch (err) {
          console.error('[FutureReleases] Error reloading data:', err);
          const errorMessage = err instanceof Error ? err.message : 'Erro ao carregar lançamentos';
          setError(errorMessage);
        } finally {
          setIsLoading(false);
        }
      };
      loadData();
    }
  }, [activeProfile?.id, user, isEncryptionReady, isCheckingEncryption]);

  // Save releases to localStorage when they change (fallback only)
  useEffect(() => {
    if (!isLoading && futureReleases.length >= 0) {
      localStorage.setItem('futureReleases', JSON.stringify(futureReleases));
      console.log('[FutureReleases] Backup saved to localStorage:', {
        totalReleases: futureReleases.length
      });
    }
  }, [futureReleases, isLoading]);

  // Save groups to localStorage when they change (fallback only)
  useEffect(() => {
    if (!isLoading && installmentGroups.length >= 0) {
      localStorage.setItem('installmentGroups', JSON.stringify(installmentGroups));
    }
  }, [installmentGroups, isLoading]);

  // Listen for account deletion events to remove orphaned releases and groups from local state
  useEffect(() => {
    const handleAccountDeleted = (event: Event) => {
      const customEvent = event as CustomEvent<{ accountId: string }>;
      const { accountId } = customEvent.detail;
      
      console.log('[FutureReleases] Account deleted event received, filtering data', { accountId });
      
      setFutureReleases(prev => prev.filter(r => r.accountId !== accountId));
      setInstallmentGroups(prev => prev.filter(g => g.accountId !== accountId));
    };

    window.addEventListener('account-deleted', handleAccountDeleted);
    return () => window.removeEventListener('account-deleted', handleAccountDeleted);
  }, []);

  const addFutureRelease = async (
    releaseData: FutureReleaseFormData & { installmentGroupId?: string; installmentNumber?: number; }
  ) => {
    const newRelease = createFutureRelease(releaseData);
    
    console.log(`[FutureReleases] Adding future release: ${newRelease.name}`);
    console.log(`[FutureReleases] Recurrent: ${newRelease.isRecurrent}, Type: ${newRelease.type}`);
    
    try {
      // Save to Supabase first
      if (await isUserAuthenticated()) {
        await saveFutureReleaseToSupabase(newRelease);
        console.log('[FutureReleases] Release saved to Supabase:', newRelease.id);
      }
      
      // Update local state
      setFutureReleases(prev => [...prev, newRelease]);
    } catch (error) {
      console.error('[FutureReleases] Error saving release:', error);
      setError('Erro ao criar lançamento');
      throw error;
    }
  };

  const updateFutureRelease = async (id: string, updatedData: Partial<FutureRelease> & { paidDate?: string }) => {
    console.log(`[FutureReleases] Updating release: ${id}`);
    
    try {
      const updatedReleases = updateFutureReleaseData(futureReleases, id, updatedData);
      const updatedRelease = updatedReleases.find(r => r.id === id);
      
      if (updatedRelease && await isUserAuthenticated()) {
        await saveFutureReleaseToSupabase(updatedRelease);
        console.log('[FutureReleases] Updated release saved to Supabase:', id);
      }
      
      setFutureReleases(updatedReleases);
    } catch (error) {
      console.error('[FutureReleases] Error updating release:', error);
      setError('Erro ao atualizar lançamento');
      throw error;
    }
  };

  const deleteFutureRelease = async (id: string) => {
    console.log(`[FutureReleases] Deleting release: ${id}`);
    
    try {
      // Delete from Supabase first
      if (await isUserAuthenticated()) {
        await deleteFutureReleaseFromSupabase(id);
        console.log('[FutureReleases] Release deleted from Supabase:', id);
      }
      
      // Update local state
      setFutureReleases(prev => prev.filter(release => release.id !== id));
    } catch (error) {
      console.error('[FutureReleases] Error deleting release:', error);
      setError('Erro ao excluir lançamento');
      throw error;
    }
  };

  const getFutureReleaseById = (id: string) => {
    return futureReleases.find(release => release.id === id);
  };

  const cancelFutureRelease = async (id: string) => {
    await updateFutureRelease(id, { status: 'cancelado' });
  };

  const activateFutureRelease = async (id: string) => {
    await updateFutureRelease(id, { status: 'ativo' });
  };

  const addInstallmentGroup = async (group: InstallmentGroup, releases: any[]) => {
    console.log(`[FutureReleases] Adding installment group: ${group.name}`);
    
    try {
      // Save to Supabase first
      if (await isUserAuthenticated()) {
        await saveInstallmentGroupToSupabase(group);
        console.log('[FutureReleases] Group saved to Supabase:', group.id);
      }
      
      // Update local state
      setInstallmentGroups(prev => [...prev, group]);
      
      // Add related releases
      for (const release of releases) {
        await addFutureRelease(release);
      }
    } catch (error) {
      console.error('[FutureReleases] Error saving group:', error);
      setError('Erro ao criar grupo de parcelas');
      throw error;
    }
  };

  const getInstallmentGroupById = (id: string) => {
    return installmentGroups.find(g => g.id === id);
  };

  return (
    <FutureReleasesContext.Provider
      value={{
        futureReleases,
        addFutureRelease,
        updateFutureRelease,
        deleteFutureRelease,
        getFutureReleaseById,
        cancelFutureRelease,
        activateFutureRelease,
        installmentGroups,
        addInstallmentGroup,
        getInstallmentGroupById,
        isLoading,
        error
      }}
    >
      {children}
    </FutureReleasesContext.Provider>
  );
};
